import React, { useState, useEffect, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { DatePicker } from "@/components/ui/date-picker"
import { useAuthStore } from "@/store/auth.store"
import { useGetAdminToBuyerQuotationById } from "@/hooks/admin/useGetAdminToBuyerQuotationById"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { useGetSellerQuotationById } from "@/hooks/admin/useGetSellerQuotationById"
import { useSendQuotationToBuyer } from "@/hooks/admin/useSendQuotationToBuyer"
import { useReviseAdminQuotation } from "@/hooks/admin/useReviseAdminQuotation"
import { useRequestSellerRevision } from "@/hooks/admin/useRequestSellerRevision"
import { useCountryCurrencies } from "@/hooks/useCountryCurrencies"
import { useNavigate } from "@tanstack/react-router"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar, RfqDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { cn } from "@/lib/utils"
import {
  Loader2,
  AlertCircle,
  FileText,
  Calendar,
  Package,
  Plus,
  ChevronLeft,
  ChevronDown,
  SlidersHorizontal,
  Check,
  Trash2,
  Edit,
  Building2,
  Sparkles,
  Layers,
  Tag,
  Hash,
  MapPin,
  MoreVertical,
  RotateCcw,
  X,
} from "lucide-react"

export const QUANTITY_UNITS = [
  { code: "kg", name: "Kilogram (kg)" },
  { code: "g", name: "Gram (g)" },
  { code: "ltr", name: "Liter (ltr)" },
  { code: "ml", name: "Milliliter (ml)" },
  { code: "pcs", name: "Pieces (pcs)" },
  { code: "qtl", name: "Quintal (qtl)" },
  { code: "ton", name: "Metric Ton (ton)" },
  { code: "box", name: "Box (box)" },
]

const isIntegerUnit = (unit?: string | null): boolean => {
  if (!unit) return false
  const u = unit.toLowerCase().trim()
  return u === "pcs" || u === "box" || u.includes("piece") || u.includes("box")
}

export interface ChargeItem {
  description: string;
  unitPrice: number | string;
  discount: number | string;
  quantity: number | string;
  unit?: string;
  tax: number | string;
  taxType: string;
  productVariantId?: string;
}

export const DEFAULT_CHARGE: ChargeItem = {
  description: "Shipping Fee",
  unitPrice: "",
  discount: "0",
  quantity: "",
  unit: "",
  tax: "18",
  taxType: "GST",
  productVariantId: ""
};

export function getChargeDescriptionError(val: string): string | null {
  if (val.length > 50) {
    return "Max 50 characters"
  }
  return null
}

export function getChargeUnitPriceError(val: string | number | ""): string | null {
  if (val === "") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Must be > 0"
  }
  if (num > 10_000_000) {
    return "Max 10,000,000"
  }
  return null
}

export function getChargeQuantityError(val: string | number | "" | null | undefined, unit?: string): string | null {
  if (val === "" || val === null || val === undefined) return null
  const str = String(val)
  if (isIntegerUnit(unit) && str.includes(".")) {
    return "Whole numbers only"
  }
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num <= 0) {
    return "Must be > 0"
  }
  if (num >= 100_000) {
    return "Max 100,000"
  }
  return null
}

export function getChargeUnitError(unit: string | undefined, quantity: string | number | "" | null | undefined): string | null {
  const hasQuantity = quantity !== "" && quantity !== undefined && quantity !== null
  if (hasQuantity && (!unit || !unit.trim())) {
    return "Select unit"
  }
  return null
}

export function getChargeDiscountError(val: string | number | ""): string | null {
  if (val === "" || val === 0 || val === "0") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num < 0 || num >= 100) {
    return "Must be 0-99.99%"
  }
  return null
}

export function getChargeTaxError(val: string | number | ""): string | null {
  if (val === "" || val === 0 || val === "0") return null
  const str = String(val)
  if (str.includes(".") && str.split(".")[1]?.length > 2) {
    return "Max 2 decimals"
  }
  const num = Number(val)
  if (isNaN(num) || num < 0 || num >= 100) {
    return "Must be 0-99.99%"
  }
  return null
}

export function isChargeRowCompleteAndValid(charge: ChargeItem): boolean {
  if (!charge.description.trim()) return false
  if (getChargeDescriptionError(charge.description) !== null) return false
  if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice))) return false
  if (getChargeUnitPriceError(charge.unitPrice) !== null) return false
  
  const hasQuantity = charge.quantity !== "" && charge.quantity !== undefined && charge.quantity !== null
  if (hasQuantity) {
    if (isNaN(Number(charge.quantity))) return false
    if (getChargeQuantityError(charge.quantity, charge.unit) !== null) return false
    if (!charge.unit || !charge.unit.trim()) return false
  }

  if (getChargeDiscountError(charge.discount) !== null) return false
  if (getChargeTaxError(charge.tax) !== null) return false
  return true
}

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function getTodayDateString() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

function QuotationDetailView({
  quotation,
  activeTab,
  onBack,
  onForwardSuccess,
  onRevised
}: {
  quotation: any
  activeTab: "seller" | "buyer"
  onBack: () => void
  onForwardSuccess: (msg: string) => void
  onRevised?: (newId: string) => void
}) {
  const navigate = useNavigate()
  const [selectedDetailVariant, setSelectedDetailVariant] = useState<any | null>(null)
  const [selectedDetailVariantId, setSelectedDetailVariantId] = useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null)
  const [selectedRfq, setSelectedRfq] = useState<any | null>(null)
  const charges = quotation.sellerGeneratedQuotationCharges || quotation.adminToBuyerQuotationCharges || quotation.charges || []
  const totalAmount = useMemo(() => {
    return charges.reduce((sum: number, c: any) => {
      if (c.amount !== undefined) return sum + Number(c.amount)
      const qty = (c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1
      const base = Number(c.unitPrice || 0) * qty
      const sub = base - (base * Number(c.discount || 0) / 100)
      const tot = sub + (sub * Number(c.tax || 0) / 100)
      return sum + tot
    }, 0)
  }, [charges])

  const [message, setMessage] = useState(quotation.message || "")
  const [currencyCode, setCurrencyCode] = useState(quotation.currencyCode || "INR")
  const [validUntil, setValidUntil] = useState(() => {
    const todayStr = getTodayDateString()
    const raw = quotation.expiresAt || quotation.validUntil || ""
    const d = raw ? raw.split("T")[0] : ""
    return d && d < todayStr ? todayStr : d
  })
  const [formCharges, setFormCharges] = useState<ChargeItem[]>([{ ...DEFAULT_CHARGE }])
  const [formError, setFormError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false)
  const [currencySearch, setCurrencySearch] = useState("")
  const currencyDropdownRef = useRef<HTMLDivElement>(null)
  const { data: countryCurrenciesData, isLoading: isLoadingCurrencies } = useCountryCurrencies()

  const currencyOptions = useMemo(() => {
    return (countryCurrenciesData && countryCurrenciesData.length > 0)
      ? countryCurrenciesData.map((item) => ({
          id: item.id,
          code: item.code,
          country: item.country,
          name: `${item.code} - ${item.country}`,
        }))
      : [
          { id: 105, code: "INR", country: "INDIA", name: "INR - INDIA" },
          { id: 2, code: "USD", country: "UNITED STATES", name: "USD - UNITED STATES" },
          { id: 3, code: "EUR", country: "EUROPEAN UNION", name: "EUR - EUROPEAN UNION" },
          { id: 4, code: "GBP", country: "UNITED KINGDOM", name: "GBP - UNITED KINGDOM" },
          { id: 5, code: "AED", country: "UNITED ARAB EMIRATES", name: "AED - UNITED ARAB EMIRATES" },
          { id: 6, code: "SGD", country: "SINGAPORE", name: "SGD - SINGAPORE" },
        ]
  }, [countryCurrenciesData])

  const filteredCurrencies = useMemo(() => {
    if (!currencySearch.trim()) return currencyOptions
    const q = currencySearch.toLowerCase().trim()
    return currencyOptions.filter((curr) => curr.code.toLowerCase().includes(q) || curr.name.toLowerCase().includes(q))
  }, [currencyOptions, currencySearch])
  const [showDropdown, setShowDropdown] = useState(false)
  const [openForwardTaxTypeDropdownIndex, setOpenForwardTaxTypeDropdownIndex] = useState<number | null>(null)
  const [forwardTaxTypeDropdownPosition, setForwardTaxTypeDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const forwardTaxTypeDropdownMenuRef = useRef<HTMLUListElement>(null)

  const [openForwardUnitDropdownIndex, setOpenForwardUnitDropdownIndex] = useState<number | null>(null)
  const [forwardUnitDropdownPosition, setForwardUnitDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const forwardUnitDropdownMenuRef = useRef<HTMLUListElement>(null)

  const canAddCharge = formCharges.every(isChargeRowCompleteAndValid)

  const handleAddCharge = () => {
    if (formCharges.length > 1) {
      const isPreviousRowsValid = formCharges.slice(0, formCharges.length - 1).every(isChargeRowCompleteAndValid)
      const isLatestRowValid = isChargeRowCompleteAndValid(formCharges[formCharges.length - 1])

      if (!isPreviousRowsValid) {
        toast.error("Please complete the validation of the current charge before adding another.")
        return
      }
      if (!isLatestRowValid) {
        toast.error("Please first finish the latest added row before adding another.")
        return
      }
    } else if (!canAddCharge) {
      toast.error("Please complete the validation of the current charge before adding another.")
      return
    }
    setFormCharges([...formCharges, { ...DEFAULT_CHARGE }])
  }

  const handleRemoveCharge = (index: number) => {
    setFormCharges(formCharges.filter((_, i) => i !== index))
  }

  const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>, currentVal?: any, isIntegerOnly?: boolean) => {
    if (
      e.key === "-" ||
      e.key === "Subtract" ||
      e.code === "Minus" ||
      e.code === "NumpadSubtract" ||
      e.key === "+" ||
      e.key === "e" ||
      e.key === "E"
    ) {
      e.preventDefault()
    }
    if (isIntegerOnly && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
    if (e.key === "." && currentVal !== undefined && String(currentVal).includes(".")) {
      e.preventDefault()
    }
  }

  const handleChargeChange = (index: number, field: keyof ChargeItem, value: any) => {
    let sanitized = value
    const currentUnit = field === "unit" ? value : formCharges[index]?.unit

    if (typeof sanitized === "string") {
      if (field === "quantity") {
        if (isIntegerUnit(currentUnit)) {
          sanitized = sanitized.split(".")[0].replace(/\D/g, "")
        } else {
          sanitized = sanitized.replace(/[^0-9.]/g, "")
          const parts = sanitized.split(".")
          if (parts.length > 2) {
            sanitized = parts[0] + "." + parts.slice(1).join("")
          }
        }
      } else if (["unitPrice", "discount", "tax"].includes(field as string)) {
        sanitized = sanitized.replace(/[^0-9.]/g, "")
        const parts = sanitized.split(".")
        if (parts.length > 2) {
          sanitized = parts[0] + "." + parts.slice(1).join("")
        }
      }
    }

    const updated = [...formCharges]
    const updatedCharge = { ...updated[index], [field]: sanitized }

    // If unit changed to Piece or Box, trim out decimal values from existing quantity
    if (field === "unit" && isIntegerUnit(sanitized)) {
      const currentQty = String(updatedCharge.quantity ?? "")
      if (currentQty.includes(".")) {
        updatedCharge.quantity = currentQty.split(".")[0].replace(/\D/g, "")
      }
    }

    updated[index] = updatedCharge
    setFormCharges(updated)
  }

  useEffect(() => {
    if (!formError) return
    const scrollTimer = setTimeout(() => {
      document.getElementById("admin-forward-charges-section")?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 50)
    const dismissTimer = setTimeout(() => {
      setFormError(null)
    }, 5000)
    return () => {
      clearTimeout(scrollTimer)
      clearTimeout(dismissTimer)
    }
  }, [formError])

  useEffect(() => {
    setMessage(quotation.message || "")
    setCurrencyCode(quotation.currencyCode || "INR")
    const todayStr = getTodayDateString()
    const rawValid = quotation.expiresAt || quotation.validUntil || ""
    const dPart = rawValid ? rawValid.split("T")[0] : ""
    setValidUntil(dPart && dPart < todayStr ? todayStr : dPart)
    if (charges && charges.length > 0) {
      setFormCharges(charges.map((c: any) => ({
        description: c.description || "",
        unitPrice: c.unitPrice !== undefined ? String(c.unitPrice) : (c.amount !== undefined ? String(c.amount) : ""),
        quantity: c.quantity !== undefined && c.quantity !== null ? String(c.quantity) : "",
        unit: c.measureUnit || c.unit || "",
        discount: c.discount !== undefined ? String(c.discount) : "0",
        tax: c.tax !== undefined ? String(c.tax) : "18",
        taxType: c.taxType || "GST",
        productVariantId: c.productVariantId || "",
      })))
    } else {
      setFormCharges([{ ...DEFAULT_CHARGE }])
    }
    setFormError(null)
    setSuccessMessage(null)
  }, [quotation, charges])
  
  const sendMutation = useSendQuotationToBuyer()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const reportValidationError = (errMsg: string) => {
      setFormError(errMsg)
      setTimeout(() => {
        document.getElementById("admin-forward-charges-section")?.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 50)
    }

    if (!message.trim()) {
      reportValidationError("Message / Terms is required.")
      return
    }
    if (!currencyCode.trim()) {
      reportValidationError("Currency code is required.")
      return
    }
    if (formCharges.length === 0) {
      reportValidationError("At least one charge is required.")
      return
    }

    for (let i = 0; i < formCharges.length; i++) {
      const charge = formCharges[i]
      const descErr = getChargeDescriptionError(charge.description)
      const unitPriceErr = getChargeUnitPriceError(charge.unitPrice)
      const quantityErr = getChargeQuantityError(charge.quantity, charge.unit)
      const discountErr = getChargeDiscountError(charge.discount)
      const taxErr = getChargeTaxError(charge.tax)

      if (!charge.description.trim()) {
        reportValidationError(`Description for charge #${i + 1} is required.`)
        return
      }
      if (descErr) {
        reportValidationError(`Charge #${i + 1} Description: ${descErr}`)
        return
      }
      if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice))) {
        reportValidationError(`Unit price for charge #${i + 1} is required.`)
        return
      }
      if (unitPriceErr) {
        reportValidationError(`Charge #${i + 1} Unit Price: ${unitPriceErr}`)
        return
      }
      const hasQuantity = charge.quantity !== "" && charge.quantity !== undefined && charge.quantity !== null
      if (hasQuantity) {
        if (isNaN(Number(charge.quantity))) {
          reportValidationError(`Please enter a valid quantity for charge #${i + 1}.`)
          return
        }
        if (quantityErr) {
          reportValidationError(`Charge #${i + 1} Quantity: ${quantityErr}`)
          return
        }
        if (!charge.unit || !charge.unit.trim()) {
          reportValidationError(`Please select a unit for charge #${i + 1}.`)
          return
        }
      }
      if (discountErr) {
        reportValidationError(`Charge #${i + 1} Discount: ${discountErr}`)
        return
      }
      if (taxErr) {
        reportValidationError(`Charge #${i + 1} Tax: ${taxErr}`)
        return
      }
      if (!isChargeRowCompleteAndValid(charge)) {
        reportValidationError(`Please ensure all fields in charge #${i + 1} are complete and valid.`)
        return
      }
    }

    const todayStr = getTodayDateString()
    const effectiveValidUntil = validUntil && validUntil < todayStr ? todayStr : validUntil

    const payload = {
      rfqId: quotation.rfqId,
      adminToSellerRfqId: quotation.adminToSellerRfqId,
      companyId: quotation.companyId,
      message: message.trim(),
      currencyCode: currencyCode.trim(),
      expiresAt: effectiveValidUntil ? (effectiveValidUntil.includes("T") ? effectiveValidUntil : `${effectiveValidUntil}T12:00:00.000Z`) : undefined,
      charges: formCharges.map((c) => ({
        description: c.description.trim(),
        unitPrice: Number(c.unitPrice),
        quantity: c.quantity !== "" && c.quantity !== undefined && c.quantity !== null ? Number(c.quantity) : null,
        measureUnit: (c.quantity !== "" && c.quantity !== undefined && c.quantity !== null) ? (c.unit?.trim() || undefined) : undefined,
        discount: Number(c.discount || 0),
        tax: Number(c.tax || 0),
        taxType: c.taxType,
        productVariantId: c.productVariantId || undefined,
      })),
      sellerToAdminQuotationId: quotation.id,
    }

    try {
      await sendMutation.mutateAsync(payload)
      onForwardSuccess("Quotation forwarded to buyer successfully!")
      navigate({ to: "/quotations", search: { subTab: "buyer" } })
    } catch (err: any) {
      setFormError(err?.message || "Failed to forward quotation. Please try again.")
    }
  }

  const [isRevising, setIsRevising] = useState(false)
  const [revisedMsg, setRevisedMsg] = useState("")
  const [revisedCurrency, setRevisedCurrency] = useState("")
  const [revisedValidUntil, setRevisedValidUntil] = useState("")
  const [isRevisedCurrencyDropdownOpen, setIsRevisedCurrencyDropdownOpen] = useState(false)
  const [revisedCurrencySearch, setRevisedCurrencySearch] = useState("")
  const revisedCurrencyDropdownRef = useRef<HTMLDivElement>(null)

  const filteredRevisedCurrencies = useMemo(() => {
    if (!revisedCurrencySearch.trim()) return currencyOptions
    const q = revisedCurrencySearch.toLowerCase().trim()
    return currencyOptions.filter((curr) => curr.code.toLowerCase().includes(q) || curr.name.toLowerCase().includes(q))
  }, [currencyOptions, revisedCurrencySearch])
  const [revisedCharges, setRevisedCharges] = useState<ChargeItem[]>([{ ...DEFAULT_CHARGE }])
  const [revisedFormError, setRevisedFormError] = useState<string | null>(null)
  const [openReviseTaxTypeDropdownIndex, setOpenReviseTaxTypeDropdownIndex] = useState<number | null>(null)
  const [reviseTaxTypeDropdownPosition, setReviseTaxTypeDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const reviseTaxTypeDropdownMenuRef = useRef<HTMLUListElement>(null)

  const [openReviseUnitDropdownIndex, setOpenReviseUnitDropdownIndex] = useState<number | null>(null)
  const [reviseUnitDropdownPosition, setReviseUnitDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const reviseUnitDropdownMenuRef = useRef<HTMLUListElement>(null)

  const canAddRevisedCharge = revisedCharges.every(isChargeRowCompleteAndValid)

  const handleAddRevisedCharge = () => {
    if (revisedCharges.length > 1) {
      const isPreviousRowsValid = revisedCharges.slice(0, revisedCharges.length - 1).every(isChargeRowCompleteAndValid)
      const isLatestRowValid = isChargeRowCompleteAndValid(revisedCharges[revisedCharges.length - 1])

      if (!isPreviousRowsValid) {
        toast.error("Please complete the validation of the current charge before adding another.")
        return
      }
      if (!isLatestRowValid) {
        toast.error("Please first finish the latest added row before adding another.")
        return
      }
    } else if (!canAddRevisedCharge) {
      toast.error("Please complete the validation of the current charge before adding another.")
      return
    }
    setRevisedCharges([...revisedCharges, { ...DEFAULT_CHARGE }])
  }

  const handleRemoveRevisedCharge = (index: number) => {
    setRevisedCharges(revisedCharges.filter((_, i) => i !== index))
  }

  const handleRevisedChargeChange = (index: number, field: keyof ChargeItem, value: any) => {
    let sanitized = value
    const currentUnit = field === "unit" ? value : revisedCharges[index]?.unit

    if (typeof sanitized === "string") {
      if (field === "quantity") {
        if (isIntegerUnit(currentUnit)) {
          sanitized = sanitized.split(".")[0].replace(/\D/g, "")
        } else {
          sanitized = sanitized.replace(/[^0-9.]/g, "")
          const parts = sanitized.split(".")
          if (parts.length > 2) {
            sanitized = parts[0] + "." + parts.slice(1).join("")
          }
        }
      } else if (["unitPrice", "discount", "tax"].includes(field as string)) {
        sanitized = sanitized.replace(/[^0-9.]/g, "")
        const parts = sanitized.split(".")
        if (parts.length > 2) {
          sanitized = parts[0] + "." + parts.slice(1).join("")
        }
      }
    }

    const updated = [...revisedCharges]
    const updatedCharge = { ...updated[index], [field]: sanitized }

    // If unit changed to Piece or Box, trim out decimal values from existing quantity
    if (field === "unit" && isIntegerUnit(sanitized)) {
      const currentQty = String(updatedCharge.quantity ?? "")
      if (currentQty.includes(".")) {
        updatedCharge.quantity = currentQty.split(".")[0].replace(/\D/g, "")
      }
    }

    updated[index] = updatedCharge
    setRevisedCharges(updated)
  }

  useEffect(() => {
    if (!revisedFormError) return
    const scrollTimer = setTimeout(() => {
      document.getElementById("admin-revise-charges-section")?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 50)
    const dismissTimer = setTimeout(() => {
      setRevisedFormError(null)
    }, 5000)
    return () => {
      clearTimeout(scrollTimer)
      clearTimeout(dismissTimer)
    }
  }, [revisedFormError])

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setIsCurrencyDropdownOpen(false)
        setCurrencySearch("")
      }
      if (revisedCurrencyDropdownRef.current && !revisedCurrencyDropdownRef.current.contains(e.target as Node)) {
        setIsRevisedCurrencyDropdownOpen(false)
        setRevisedCurrencySearch("")
      }
      if (openForwardTaxTypeDropdownIndex !== null) {
        const isMenu = forwardTaxTypeDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="forward-taxtype-${openForwardTaxTypeDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenForwardTaxTypeDropdownIndex(null)
        }
      }
      if (openForwardUnitDropdownIndex !== null) {
        const isMenu = forwardUnitDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="forward-unit-${openForwardUnitDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenForwardUnitDropdownIndex(null)
        }
      }
      if (openReviseTaxTypeDropdownIndex !== null) {
        const isMenu = reviseTaxTypeDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="revise-taxtype-${openReviseTaxTypeDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenReviseTaxTypeDropdownIndex(null)
        }
      }
      if (openReviseUnitDropdownIndex !== null) {
        const isMenu = reviseUnitDropdownMenuRef.current?.contains(e.target as Node)
        const isTrigger = (e.target as HTMLElement)?.closest?.(`[data-dropdown-trigger="revise-unit-${openReviseUnitDropdownIndex}"]`)
        if (!isMenu && !isTrigger) {
          setOpenReviseUnitDropdownIndex(null)
        }
      }
    }
    const handleScroll = (e: Event) => {
      if (
        currencyDropdownRef.current?.contains(e.target as Node) ||
        revisedCurrencyDropdownRef.current?.contains(e.target as Node) ||
        forwardTaxTypeDropdownMenuRef.current?.contains(e.target as Node) ||
        forwardUnitDropdownMenuRef.current?.contains(e.target as Node) ||
        reviseTaxTypeDropdownMenuRef.current?.contains(e.target as Node) ||
        reviseUnitDropdownMenuRef.current?.contains(e.target as Node)
      ) {
        return
      }
      setOpenForwardTaxTypeDropdownIndex(null)
      setOpenForwardUnitDropdownIndex(null)
      setOpenReviseTaxTypeDropdownIndex(null)
      setOpenReviseUnitDropdownIndex(null)
    }

    document.addEventListener("mousedown", handleOutsideClick)
    window.addEventListener("scroll", handleScroll, true)
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      window.removeEventListener("scroll", handleScroll, true)
    }
  }, [openForwardTaxTypeDropdownIndex, openForwardUnitDropdownIndex, openReviseTaxTypeDropdownIndex, openReviseUnitDropdownIndex])

  const reviseMutation = useReviseAdminQuotation()
  const requestSellerRevisionMutation = useRequestSellerRevision()

  const startRevision = () => {
    setRevisedMsg(quotation.message || "")
    setRevisedCurrency(quotation.currencyCode || "INR")
    const todayStr = getTodayDateString()
    const rawValidUntil = quotation.expiresAt || quotation.validUntil || ""
    const datePart = rawValidUntil ? rawValidUntil.split("T")[0] : ""
    const initialValidUntil = datePart && datePart < todayStr ? todayStr : datePart

    setRevisedValidUntil(initialValidUntil)
    setRevisedCharges(
      charges && charges.length > 0
        ? charges.map((c: any) => ({
            description: c.description || "",
            unitPrice: c.unitPrice !== undefined ? String(c.unitPrice) : (c.amount !== undefined ? String(c.amount) : ""),
            quantity: c.quantity !== undefined && c.quantity !== null ? String(c.quantity) : "",
            unit: c.measureUnit || c.unit || "",
            discount: c.discount !== undefined ? String(c.discount) : "0",
            tax: c.tax !== undefined ? String(c.tax) : "18",
            taxType: c.taxType || "GST",
            productVariantId: c.productVariantId || "",
          }))
        : [{ ...DEFAULT_CHARGE }]
    )
    setRevisedFormError(null)
    setIsRevising(true)
  }

  const handleReviseSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setRevisedFormError(null)

    const reportValidationError = (errMsg: string) => {
      setRevisedFormError(errMsg)
      setTimeout(() => {
        document.getElementById("admin-revise-charges-section")?.scrollIntoView({ behavior: "smooth", block: "center" })
      }, 50)
    }

    if (!revisedMsg.trim()) {
      reportValidationError("Quotation message is required.")
      return
    }
    if (!revisedCurrency.trim()) {
      reportValidationError("Currency code is required.")
      return
    }
    if (revisedCharges.length === 0) {
      reportValidationError("At least one charge is required.")
      return
    }

    for (let i = 0; i < revisedCharges.length; i++) {
      const charge = revisedCharges[i]
      const descErr = getChargeDescriptionError(charge.description)
      const unitPriceErr = getChargeUnitPriceError(charge.unitPrice)
      const quantityErr = getChargeQuantityError(charge.quantity, charge.unit)
      const discountErr = getChargeDiscountError(charge.discount)
      const taxErr = getChargeTaxError(charge.tax)

      if (!charge.description.trim()) {
        reportValidationError(`Description for charge #${i + 1} is required.`)
        return
      }
      if (descErr) {
        reportValidationError(`Charge #${i + 1} Description: ${descErr}`)
        return
      }
      if (charge.unitPrice === "" || isNaN(Number(charge.unitPrice))) {
        reportValidationError(`Unit price for charge #${i + 1} is required.`)
        return
      }
      if (unitPriceErr) {
        reportValidationError(`Charge #${i + 1} Unit Price: ${unitPriceErr}`)
        return
      }
      const hasQuantity = charge.quantity !== "" && charge.quantity !== undefined && charge.quantity !== null
      if (hasQuantity) {
        if (isNaN(Number(charge.quantity))) {
          reportValidationError(`Please enter a valid quantity for charge #${i + 1}.`)
          return
        }
        if (quantityErr) {
          reportValidationError(`Charge #${i + 1} Quantity: ${quantityErr}`)
          return
        }
        if (!charge.unit || !charge.unit.trim()) {
          reportValidationError(`Please select a unit for charge #${i + 1}.`)
          return
        }
      }
      if (discountErr) {
        reportValidationError(`Charge #${i + 1} Discount: ${discountErr}`)
        return
      }
      if (taxErr) {
        reportValidationError(`Charge #${i + 1} Tax: ${taxErr}`)
        return
      }
      if (!isChargeRowCompleteAndValid(charge)) {
        reportValidationError(`Please ensure all fields in charge #${i + 1} are complete and valid.`)
        return
      }
    }

    const todayStr = getTodayDateString()
    const effectiveValidUntil = revisedValidUntil && revisedValidUntil < todayStr ? todayStr : revisedValidUntil

    try {
      const res = await reviseMutation.mutateAsync({
        id: quotation.id,
        payload: {
          message: revisedMsg.trim(),
          currencyCode: revisedCurrency.trim(),
          expiresAt: effectiveValidUntil ? (effectiveValidUntil.includes("T") ? effectiveValidUntil : `${effectiveValidUntil}T12:00:00.000Z`) : undefined,
          sellerToAdminQuotationId: quotation.sellerToAdminQuotationId || quotation.sellerToAdminQuotation?.id,
          charges: revisedCharges.map((c) => ({
            description: c.description.trim(),
            unitPrice: Number(c.unitPrice),
            quantity: c.quantity !== "" && c.quantity !== undefined && c.quantity !== null ? Number(c.quantity) : null,
            measureUnit: (c.quantity !== "" && c.quantity !== undefined && c.quantity !== null) ? (c.unit?.trim() || undefined) : undefined,
            discount: Number(c.discount || 0),
            tax: Number(c.tax || 0),
            taxType: c.taxType,
            productVariantId: c.productVariantId || undefined,
          })),
        },
      })
      const newQuotation = res?.adminToBuyerQuotation
      if (newQuotation && newQuotation.id) {
        onRevised?.(newQuotation.id)
      }
      setIsRevising(false)
    } catch (err: any) {
      setRevisedFormError(err?.message || "Failed to revise quotation. Please try again.")
    }
  }

  const isForwarding = activeTab === "seller" && quotation.status === "PENDING" && !successMessage
  const isEditing = isForwarding || isRevising

  return (
    <div className={cn("animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto", isEditing && "pb-64 sm:pb-48 md:pb-16")}>
      <div className="group relative overflow-hidden bg-white border border-[#be001e26] hover:border-[#be001e4d] rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"
            >
              <ChevronLeft className="size-4" />
            </button>
            <div>
              <h3 className="text-sm text-slate-700 font-medium">
                {activeTab === "seller" ? "Incoming Seller Quotation Details" : "Sent Buyer Quotation Details"}
              </h3>
            </div>
          </div>

          <div className="flex items-center justify-between w-full md:w-auto md:justify-end gap-2">
            <span className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider shrink-0 ${
              quotation.status === "APPROVED"
                ? "bg-[#e0f2fe] text-[#0369a1] border-sky-200"
                : quotation.status === "PENDING"
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : "bg-red-50 text-red-700 border-red-100"
            }`}>
              {quotation.status === "APPROVED" && <Check className="size-3" />}
              STATUS: {quotation.status}
            </span>

            {!isRevising && activeTab === "buyer" && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="p-1.5 text-slate-500 hover:text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/30 rounded-lg transition-all border border-slate-200 cursor-pointer flex items-center justify-center shadow-sm"
                  title="More actions"
                >
                  <MoreVertical className="size-3" />
                </button>
                {showDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-30" 
                      onClick={() => setShowDropdown(false)} 
                    />
                    <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40 overflow-hidden font-sans">
                      <button
                        type="button"
                        onClick={() => {
                          setShowDropdown(false)
                          startRevision()
                        }}
                        className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:text-[#be001e] hover:bg-[#be001e]/5 transition-colors flex items-center gap-2 cursor-pointer"
                      >
                        <Edit className="size-3" />
                        Revise Quotation Now
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          setShowDropdown(false)
                          try {
                            await requestSellerRevisionMutation.mutateAsync(quotation.id)
                            toast.success("Seller revision requested successfully!")
                          } catch (err: any) {
                            toast.error(err?.message || "Failed to request seller revision.")
                          }
                        }}
                        disabled={requestSellerRevisionMutation.isPending}
                        className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:text-[#be001e] hover:bg-[#be001e]/5 transition-colors flex items-center gap-2 cursor-pointer disabled:opacity-50"
                      >
                        {requestSellerRevisionMutation.isPending ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <RotateCcw className="size-3" />
                        )}
                        Request Seller Revision
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="h-px bg-[#be001e1a] w-full" />

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            {/* Sourcing Target */}
            {(() => {
              const rfqData = quotation.rfq || quotation.adminToSellerRfq || quotation.buyerToAdminRfq || null
              if (!rfqData) return null

              const targets = (rfqData.targets || []).filter(
                (t: any) => t.product || t.productVariant || t.productId || t.productVariantId
              )

              if (targets.length === 0) return null

              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                    <Sparkles className="size-3.5 text-[#e6202f]" /> Sourcing Target Item(s)
                  </h4>
                  <div className="grid grid-cols-1 gap-2 font-sans">
                    {targets.map((target: any) => (
                      <TargetItemView 
                        key={target.id} 
                        target={target} 
                        onCompanyClick={(c) => setSelectedCompany(c)}
                        onProductClick={(p) => setSelectedProduct(p)}
                        onVariantClick={(v) => setSelectedDetailVariant(v)}
                        onExternalSellerClick={setSelectedExternalSeller}
                      />
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* Buyer Name */}
            {(quotation?.buyer || quotation?.buyerProfile || quotation?.rfq?.buyer || quotation?.adminToSellerRfq?.buyer) && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Building2 className="size-3.5 text-[#e6202f]" /> Buyer Name
                </h4>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 bg-slate-50/20 p-3 shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-slate-700 font-semibold tracking-tight font-sans">
                      {(() => {
                        const user = quotation.buyer || quotation.rfq?.buyer || quotation.adminToSellerRfq?.buyer
                        const exactName = user?.user?.name || ""
                        return exactName || "N/A"
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Company Name */}
            {quotation?.company?.legalName && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Building2 className="size-3.5 text-[#e6202f]" /> Company Name
                </h4>
                <div 
                  onClick={() => setSelectedCompany(quotation.company)}
                  className="group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-slate-50 p-3 shadow-sm transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-slate-700 font-medium hover:text-[#be001e] hover:underline tracking-tight font-sans">
                      {quotation.company.legalName}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {(() => {
              const rfqData = quotation.rfq || quotation.buyerToAdminRfq || quotation.adminToSellerRfq || null
              const rfqStatus = rfqData?.status || "UNKNOWN"
              const targetRfqMessage = activeTab === "seller" ? quotation.adminToSellerRfq?.queryMessage : quotation.rfq?.queryMessage

              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                    <FileText className="size-3.5 text-[#e6202f]" /> Linked RFQ
                  </h4>
                  <div 
                    onClick={() => {
                      if (rfqData) {
                        setSelectedRfq(rfqData)
                      }
                    }}
                    className={`group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 p-3 shadow-sm transition-all duration-200 ${rfqData ? "bg-slate-50/40 hover:bg-slate-50 hover:border-[#be001e]/30 cursor-pointer" : "bg-slate-50/20 opacity-70 cursor-default"}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-extrabold text-[#be001e] hover:underline text-xs tracking-tight font-mono">
                        {targetRfqMessage ? `${targetRfqMessage}` : "null"}
                      </span>
                    </div>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border font-sans ${
                      rfqStatus === "ACTIVE" || rfqStatus === "ACCEPTED" || rfqStatus === "OPEN"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : rfqStatus === "PENDING"
                        ? "bg-amber-50 text-amber-700 border-amber-100"
                        : rfqStatus === "CLOSED" || rfqStatus === "REJECTED"
                        ? "bg-red-50 text-red-700 border-red-100"
                        : "bg-slate-50 text-slate-700 border-slate-200"
                    }`}>
                      {rfqStatus}
                    </span>
                  </div>
                </div>
              )
            })()}

            {isRevising ? (
              <form onSubmit={handleReviseSubmit} className="space-y-4 border border-[#be001e26] p-5 rounded-2xl bg-[#be001e02] shadow-sm">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-black text-slate-600 uppercase tracking-wider flex items-center gap-1.5 font-sans">
                    <Edit className="size-4 text-[#e6202f]" /> Revise Quotation Form
                  </h4>
                  {reviseMutation.isPending && <Loader2 className="size-4 animate-spin text-[#e6202f]" />}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans">Quotation Message</label>
                    <textarea
                      value={revisedMsg}
                      onChange={(e) => setRevisedMsg(e.target.value)}
                      placeholder="Enter revised terms or message..."
                      className={cn(
                        "w-full bg-white border p-3 rounded-lg text-xs font-semibold text-slate-700 focus:outline-none transition-all duration-200 h-20 font-sans",
                        !revisedMsg.trim() && revisedFormError
                          ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                          : "border-slate-200 focus:border-[#be001e]"
                      )}
                      required
                    />
                    {!revisedMsg.trim() && revisedFormError && (
                      <p className="text-[10px] text-[#be001e] font-semibold mt-1 font-sans">
                        Quotation message is required.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:flex-nowrap gap-4 sm:gap-8 md:col-span-2">
                    <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">Currency Code</label>
                      <div className="relative w-full sm:w-[80px]" ref={revisedCurrencyDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setIsRevisedCurrencyDropdownOpen(!isRevisedCurrencyDropdownOpen)}
                          disabled={reviseMutation.isPending}
                          className={cn(
                            "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 cursor-pointer text-left font-sans",
                            !revisedCurrency.trim() && revisedFormError
                              ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                              : "border-slate-200 focus:border-[#be001e]"
                          )}
                        >
                          <span>{revisedCurrency}</span>
                          <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${isRevisedCurrencyDropdownOpen ? "rotate-180" : ""}`} />
                        </button>
                        
                        {isRevisedCurrencyDropdownOpen && (
                          <div 
                            className="absolute left-0 z-50 mt-1 w-56 sm:w-64 bg-white border border-slate-200 rounded-lg shadow-lg text-[12px] font-semibold text-slate-700 text-left overflow-hidden flex flex-col font-sans"
                          >
                            {/* Search Header - solid filled background */}
                            <div className="p-2 bg-slate-50 border-b border-slate-200 shrink-0">
                              <input
                                type="text"
                                value={revisedCurrencySearch}
                                onChange={(e) => setRevisedCurrencySearch(e.target.value)}
                                placeholder="Search currency or country..."
                                className="w-full px-2.5 py-1 text-[11px] rounded bg-white border border-slate-200 outline-none focus:border-[#be001e] text-slate-700 placeholder:text-slate-400 font-sans"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            </div>

                            {/* Scrollable list strictly below the header */}
                            <div className="max-h-[160px] overflow-y-auto py-1">
                              {isLoadingCurrencies && (
                                <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                                  <Loader2 className="size-3 animate-spin text-[#be001e]" />
                                  <span>Loading currencies...</span>
                                </div>
                              )}
                              {filteredRevisedCurrencies.map((curr, idx) => (
                                <button
                                  key={(curr as any).id ?? `${curr.code}-${idx}`}
                                  type="button"
                                  onClick={() => {
                                    setRevisedCurrency(curr.code)
                                    setIsRevisedCurrencyDropdownOpen(false)
                                    setRevisedCurrencySearch("")
                                  }}
                                  title={curr.name}
                                  className={cn(
                                    "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate block",
                                    revisedCurrency === curr.code ? "text-[#be001e] bg-red-50/30 font-bold" : ""
                                  )}
                                >
                                  {curr.name}
                                </button>
                              ))}
                              {!isLoadingCurrencies && filteredRevisedCurrencies.length === 0 && (
                                <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
                                  No currency found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0 block">Valid Until</label>
                      <div className="w-full sm:w-[170px]">
                        <DatePicker
                          value={revisedValidUntil}
                          onChange={setRevisedValidUntil}
                          min={getTodayDateString()}
                          placeholder="Select valid date"
                          disabled={reviseMutation.isPending}
                          className="!h-[34px] !py-1.5 !text-xs !bg-white !rounded-lg font-sans"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div id="admin-revise-charges-section" className="space-y-1.5 pt-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-sans">Charges Breakdown</label>
                    <button
                      type="button"
                      onClick={handleAddRevisedCharge}
                      className="text-[#be001e] hover:text-[#e6202f] font-bold text-xs flex items-center gap-0.5 cursor-pointer font-sans disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={reviseMutation.isPending}
                    >
                      <Plus className="size-3" /> Add Charge Row
                    </button>
                  </div>

                  {revisedFormError && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#e6202f] bg-red-50 p-2.5 rounded-lg border border-red-100 animate-in fade-in duration-200 font-sans my-2">
                      <AlertCircle className="size-4 shrink-0" />
                      <span>{revisedFormError}</span>
                    </div>
                  )}
                  
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm font-sans">
                    <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                      <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="p-3">Description</th>
                          <th className="p-3 text-center w-32">Unit Price</th>
                          <th className="p-3 text-center w-24">Quantity</th>
                          <th className="p-3 text-center w-28">Unit</th>
                          <th className="p-3 text-center w-28">Discount (%)</th>
                          <th className="p-3 text-center w-24">Tax (%)</th>
                          <th className="p-3 text-center w-24">Tax Type</th>
                          <th className="p-3 text-right">Total</th>
                          <th className="p-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {revisedCharges.map((charge, idx) => {
                          const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1
                          const base = (Number(charge.unitPrice) || 0) * qty
                          const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                          const total = subtotal + (subtotal * ((Number(charge.tax) || 0) / 100))

                          const descError = getChargeDescriptionError(charge.description) || (!charge.description.trim() && revisedFormError ? "Description is required" : null)
                          const unitPriceError = getChargeUnitPriceError(charge.unitPrice) || (charge.unitPrice === "" && revisedFormError ? "Unit price is required" : null)
                          const quantityError = getChargeQuantityError(charge.quantity, charge.unit)
                          const unitError = getChargeUnitError(charge.unit, charge.quantity)
                          const discountError = getChargeDiscountError(charge.discount)
                          const taxError = getChargeTaxError(charge.tax)

                          const isRowDisabled = idx > 0 && !revisedCharges.slice(0, idx).every(isChargeRowCompleteAndValid)

                          return (
                            <tr key={idx} className={cn("hover:bg-slate-50/50 transition-colors align-top", isRowDisabled && "bg-slate-50/40 opacity-70")}>
                              <td className="p-2 min-w-[200px]">
                                <input
                                  type="text"
                                  value={charge.description}
                                  onChange={(e) => handleRevisedChargeChange(idx, "description", e.target.value)}
                                  placeholder="e.g. Shipping Fee, Customs"
                                  className={cn(
                                    "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                    descError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  required
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                />
                                {descError && !isRowDisabled && (
                                  <p className="text-[10px] text-[#be001e] font-semibold mt-1">
                                    {descError}
                                  </p>
                                )}
                              </td>
                              <td className="p-2 w-32">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={charge.unitPrice}
                                  onChange={(e) => handleRevisedChargeChange(idx, "unitPrice", e.target.value)}
                                  onKeyDown={(e) => blockNegativeKeys(e, charge.unitPrice)}
                                  placeholder="0.00"
                                  className={cn(
                                    "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                    unitPriceError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  required
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                />
                                {unitPriceError && !isRowDisabled && (
                                  <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                    {unitPriceError}
                                  </p>
                                )}
                              </td>
                              <td className="p-2 w-24">
                                <input
                                  type="text"
                                  inputMode={isIntegerUnit(charge.unit) ? "numeric" : "decimal"}
                                  value={charge.quantity}
                                  onChange={(e) => handleRevisedChargeChange(idx, "quantity", e.target.value)}
                                  onKeyDown={(e) => blockNegativeKeys(e, charge.quantity, isIntegerUnit(charge.unit))}
                                  placeholder="0"
                                  className={cn(
                                    "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                    quantityError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                />
                                {quantityError && !isRowDisabled && (
                                  <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                    {quantityError}
                                  </p>
                                )}
                              </td>
                              <td className="p-2 w-28">
                                <button
                                  type="button"
                                  data-dropdown-trigger={`revise-unit-${idx}`}
                                  onClick={(e) => {
                                    if (isRowDisabled) return
                                    const rect = e.currentTarget.getBoundingClientRect()
                                    setReviseUnitDropdownPosition({
                                      top: rect.bottom,
                                      left: rect.left,
                                      width: rect.width
                                    })
                                    setOpenReviseUnitDropdownIndex(prev => prev === idx ? null : idx)
                                  }}
                                  className={cn(
                                    "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                    unitError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                >
                                  <span className="truncate">
                                    {charge.unit ? (QUANTITY_UNITS.find(u => u.code === charge.unit)?.name || charge.unit) : "Select"}
                                  </span>
                                  <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openReviseUnitDropdownIndex === idx && "rotate-180")} />
                                </button>

                                {openReviseUnitDropdownIndex === idx && reviseUnitDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                  <ul 
                                    ref={reviseUnitDropdownMenuRef}
                                    style={{
                                      position: 'fixed',
                                      top: `${reviseUnitDropdownPosition.top + 4}px`,
                                      left: `${reviseUnitDropdownPosition.left}px`,
                                      width: `${reviseUnitDropdownPosition.width}px`,
                                    }}
                                    className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                  >
                                    <li
                                      onClick={() => {
                                        handleRevisedChargeChange(idx, "unit", "")
                                        setOpenReviseUnitDropdownIndex(null)
                                      }}
                                      className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate"
                                    >
                                      None
                                    </li>
                                    {QUANTITY_UNITS.map((unitOpt) => (
                                      <li
                                        key={unitOpt.code}
                                        onClick={() => {
                                          handleRevisedChargeChange(idx, "unit", unitOpt.code)
                                          setOpenReviseUnitDropdownIndex(null)
                                        }}
                                        className={cn(
                                          "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                          charge.unit === unitOpt.code && "bg-[#be001e]/5 text-[#be001e] font-bold"
                                        )}
                                      >
                                        {unitOpt.name}
                                      </li>
                                    ))}
                                  </ul>,
                                  document.body
                                )}
                                {unitError && !isRowDisabled && (
                                  <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                    {unitError}
                                  </p>
                                )}
                              </td>
                              <td className="p-2 w-28">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={charge.discount}
                                  onChange={(e) => handleRevisedChargeChange(idx, "discount", e.target.value)}
                                  onKeyDown={(e) => blockNegativeKeys(e, charge.discount)}
                                  placeholder="0"
                                  className={cn(
                                    "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                    discountError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                />
                                {discountError && !isRowDisabled && (
                                  <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                    {discountError}
                                  </p>
                                )}
                              </td>
                              <td className="p-2 w-24">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={charge.tax}
                                  onChange={(e) => handleRevisedChargeChange(idx, "tax", e.target.value)}
                                  onKeyDown={(e) => blockNegativeKeys(e, charge.tax)}
                                  placeholder="18"
                                  className={cn(
                                    "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                    taxError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                />
                                {taxError && !isRowDisabled && (
                                  <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                    {taxError}
                                  </p>
                                )}
                              </td>
                              <td className="p-2 w-24">
                                <button
                                  type="button"
                                  data-dropdown-trigger={`revise-taxtype-${idx}`}
                                  onClick={(e) => {
                                    if (isRowDisabled) return
                                    const rect = e.currentTarget.getBoundingClientRect()
                                    setReviseTaxTypeDropdownPosition({
                                      top: rect.bottom,
                                      left: rect.left,
                                      width: rect.width
                                    })
                                    setOpenReviseTaxTypeDropdownIndex(prev => prev === idx ? null : idx)
                                  }}
                                  className={cn(
                                    "w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 focus:border-[#be001e] focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                    isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                  )}
                                  disabled={reviseMutation.isPending || isRowDisabled}
                                  title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                >
                                  <span className="truncate">
                                    {charge.taxType === "NONE" ? "None" : (charge.taxType || "GST")}
                                  </span>
                                  <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openReviseTaxTypeDropdownIndex === idx && "rotate-180")} />
                                </button>

                                {openReviseTaxTypeDropdownIndex === idx && reviseTaxTypeDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                  <ul 
                                    ref={reviseTaxTypeDropdownMenuRef}
                                    style={{
                                      position: 'fixed',
                                      top: `${reviseTaxTypeDropdownPosition.top + 4}px`,
                                      left: `${reviseTaxTypeDropdownPosition.left}px`,
                                      width: `${reviseTaxTypeDropdownPosition.width}px`,
                                    }}
                                    className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                  >
                                    {[
                                      { value: "GST", label: "GST" },
                                      { value: "VAT", label: "VAT" },
                                      { value: "NONE", label: "None" }
                                    ].map((option) => (
                                      <li
                                        key={option.value}
                                        onClick={() => {
                                          handleRevisedChargeChange(idx, "taxType", option.value)
                                          setOpenReviseTaxTypeDropdownIndex(null)
                                        }}
                                        className={cn(
                                          "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                          charge.taxType === option.value && "bg-[#be001e]/5 text-[#be001e] font-bold"
                                        )}
                                      >
                                        {option.label}
                                      </li>
                                    ))}
                                  </ul>,
                                  document.body
                                )}
                              </td>
                              <td className="p-2 text-center font-black text-slate-800 align-middle">
                                <div className="flex items-center justify-center min-h-[34px] font-mono font-bold">
                                  {Math.max(0, total).toFixed(2)}
                                </div>
                              </td>
                              <td className="p-2 text-center align-middle">
                                <div className="flex items-center justify-center min-h-[34px]">
                                  {revisedCharges.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveRevisedCharge(idx)}
                                      className="p-1.5 rounded-md border border-red-100 bg-red-50 hover:bg-red-100 text-[#e6202f] transition-all cursor-pointer shadow-sm"
                                      title="Remove charge"
                                      disabled={reviseMutation.isPending}
                                    >
                                      <X className="size-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm font-sans">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                    <span className="text-[12px] font-bold text-[#be001e] font-mono">
                      {revisedCurrency} {revisedCharges.reduce((acc, charge) => {
                        const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1
                        const base = (Number(charge.unitPrice) || 0) * qty
                        const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                        return acc + Math.max(0, subtotal + (subtotal * ((Number(charge.tax) || 0) / 100)));
                      }, 0).toFixed(2)}
                    </span>
                  </div>
                </div>

                {reviseMutation.isError && (
                  <div className="text-xs font-semibold text-[#e6202f] bg-red-50 border border-red-100 p-2.5 rounded-lg font-sans">
                    {reviseMutation.error.message || "Failed to revise quotation."}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsRevising(false)}
                    className="border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold text-xs px-4 py-2 rounded-lg transition-colors cursor-pointer font-sans"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={reviseMutation.isPending}
                    className="bg-[#be001e] hover:bg-[#e6202f] text-white font-bold text-xs px-4 py-2 rounded-lg transition-colors cursor-pointer disabled:opacity-50 font-sans flex items-center gap-1.5"
                  >
                    {reviseMutation.isPending ? (
                      <>
                        <Loader2 className="size-3 animate-spin font-sans" />
                        Submitting...
                      </>
                    ) : (
                      <>Submit Revision</>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                    <FileText className="size-3.5 text-[#e6202f]" /> Quotation Message
                  </h4>
                  <p className="text-sm text-slate-700 font-medium bg-[#be001e03] p-3.5 rounded-xl border border-[#be001e26] hover:border-[#be001e40] transition-colors leading-relaxed italic">
                    {quotation.message || "No message provided"}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                      <Package className="size-3.5 text-[#e6202f]" /> Charges Breakdown
                    </h4>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-3 font-sans">
                    {charges.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 italic text-xs">No charges specified</div>
                    ) : (
                    <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs whitespace-nowrap font-sans">
                      <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="p-3 min-w-[160px]">Description</th>
                          <th className="p-3 text-center w-32">Unit Price</th>
                          <th className="p-3 text-center w-24">Quantity</th>
                          <th className="p-3 text-center w-24">Unit</th>
                          <th className="p-3 text-center w-28">Discount (%)</th>
                          <th className="p-3 text-center w-24">Tax (%)</th>
                          <th className="p-3 text-center w-24">Tax Type</th>
                          <th className="p-3 text-right">Total</th>
                          <th className="p-3 w-20"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {charges.map((charge: any) => {
                          const isAdvanced = charge.unitPrice !== undefined;
                          const desc = charge.description || "N/A"
                          const price = isAdvanced ? Number(charge.unitPrice || 0) : Number(charge.amount || 0)
                          const qty = isAdvanced ? ((charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1) : 1
                          const disc = isAdvanced ? Number(charge.discount || 0) : 0
                          const tax = isAdvanced ? Number(charge.tax || 0) : 0
                          
                          const base = price * qty
                          const sub = base - (base * disc / 100)
                          const lineTotal = sub + (sub * tax / 100)

                          return (
                            <tr key={charge.id || charge.description} className="hover:bg-slate-50/50 transition-colors">
                              <td className="p-3 text-slate-800 font-semibold">
                                {(charge.productVariantId || charge.productVariant) ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const vId = charge.productVariantId || charge.productVariant?.id
                                      if (vId) {
                                        setSelectedDetailVariantId(vId)
                                      } else if (charge.productVariant) {
                                        setSelectedDetailVariant(charge.productVariant)
                                      }
                                    }}
                                    className="text-[#be001e] hover:text-[#e6202f] hover:underline text-left cursor-pointer transition-colors"
                                  >
                                    {desc}
                                  </button>
                                ) : (
                                  desc
                                )}
                              </td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced ? charge.unitPrice : "-"}</td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced && charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "" ? charge.quantity : "-"}</td>
                              <td className="p-3 text-center text-slate-600">{charge.measureUnit || charge.unit || "-"}</td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.discount || 0) : "-"}</td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.tax || 0) : "-"}</td>
                              <td className="p-3 text-center text-slate-600">
                                {isAdvanced && charge.taxType ? <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase border border-slate-200">{charge.taxType}</span> : "-"}
                              </td>
                              <td className="p-3 text-right text-slate-900 font-bold font-mono">
                                {lineTotal.toFixed(2)}
                              </td>
                              <td className="p-3"></td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    </div>
                    )}
                  </div>

                  <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                    <span className="text-[12px] font-bold text-[#be001e] font-mono">
                      {quotation.currencyCode} {Number(totalAmount).toFixed(2)}
                    </span>
                  </div>
                </div>



                {activeTab === "seller" && quotation.status === "PENDING" && (
                  <div className="space-y-3 font-sans">
                    <div className="flex items-center gap-1 font-sans">
                      <h4 className="text-[10px] font-black uppercase tracking-wider text-[#be001e]">
                        Forward Quotation to Buyer
                      </h4>
                    </div>

                    <div className="bg-[#be001e05] border border-[#be001e] rounded-xl p-4.5 space-y-4 shadow-[0_8px_30px_rgba(190,0,30,0.06)] transition-all duration-200">
                      {successMessage ? (
                        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800 font-semibold animate-in fade-in duration-200 font-sans">
                          <Check className="size-4 text-emerald-600 shrink-0" />
                          <span>{successMessage}</span>
                        </div>
                      ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 font-sans">
                              Message / Terms
                            </label>
                            <textarea
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              placeholder="e.g., Delivery within 5 business days."
                              className={cn(
                                "w-full min-h-[70px] max-h-[140px] rounded-lg border bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200 font-sans",
                                !message.trim() && formError
                                  ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                  : "border-slate-200 focus:border-[#be001e]"
                              )}
                              disabled={sendMutation.isPending}
                              required
                            />
                            {!message.trim() && formError && (
                              <p className="text-[10px] text-[#be001e] font-semibold mt-1 font-sans">
                                Message / Terms is required.
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center sm:flex-nowrap gap-4 sm:gap-8 relative">
                            <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 font-sans shrink-0 block">
                                Currency Code
                              </label>
                              <div className="relative w-full sm:w-[80px]" ref={currencyDropdownRef}>
                                <button
                                  type="button"
                                  onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                                  disabled={sendMutation.isPending}
                                  className={cn(
                                    "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 cursor-pointer text-left font-sans",
                                    !currencyCode.trim() && formError
                                      ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                      : "border-slate-200 focus:border-[#be001e]"
                                  )}
                                >
                                  <span>{currencyCode}</span>
                                  <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${isCurrencyDropdownOpen ? "rotate-180" : ""}`} />
                                </button>
                                
                                {isCurrencyDropdownOpen && (
                                  <div 
                                    className="absolute left-0 z-50 mt-1 w-56 sm:w-64 bg-white border border-slate-200 rounded-lg shadow-lg text-[12px] font-semibold text-slate-700 text-left overflow-hidden flex flex-col font-sans"
                                  >
                                    {/* Search Header - solid filled background */}
                                    <div className="p-2 bg-slate-50 border-b border-slate-200 shrink-0">
                                      <input
                                        type="text"
                                        value={currencySearch}
                                        onChange={(e) => setCurrencySearch(e.target.value)}
                                        placeholder="Search currency or country..."
                                        className="w-full px-2.5 py-1 text-[11px] rounded bg-white border border-slate-200 outline-none focus:border-[#be001e] text-slate-700 placeholder:text-slate-400 font-sans"
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                      />
                                    </div>

                                    {/* Scrollable list strictly below the header */}
                                    <div className="max-h-[160px] overflow-y-auto py-1">
                                      {isLoadingCurrencies && (
                                        <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
                                          <Loader2 className="size-3 animate-spin text-[#be001e]" />
                                          <span>Loading currencies...</span>
                                        </div>
                                      )}
                                      {filteredCurrencies.map((curr, idx) => (
                                        <button
                                          key={(curr as any).id ?? `${curr.code}-${idx}`}
                                          type="button"
                                          onClick={() => {
                                            setCurrencyCode(curr.code)
                                            setIsCurrencyDropdownOpen(false)
                                            setCurrencySearch("")
                                          }}
                                          title={curr.name}
                                          className={cn(
                                            "w-full text-left px-3 py-1.5 hover:bg-slate-50 transition-colors cursor-pointer truncate block",
                                            currencyCode === curr.code ? "text-[#be001e] bg-red-50/30 font-bold" : ""
                                          )}
                                        >
                                          {curr.name}
                                        </button>
                                      ))}
                                      {!isLoadingCurrencies && filteredCurrencies.length === 0 && (
                                        <div className="px-3 py-2 text-[11px] text-slate-400 text-center">
                                          No currency found
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 font-sans shrink-0 block">
                                Valid Until
                              </label>
                              <div className="w-full sm:w-[170px]">
                                <DatePicker
                                  value={validUntil}
                                  onChange={setValidUntil}
                                  min={getTodayDateString()}
                                  placeholder="Select valid date"
                                  disabled={sendMutation.isPending}
                                  className="!h-[34px] !py-1.5 !text-xs !bg-white !rounded-lg font-sans"
                                />
                              </div>
                            </div>
                          </div>

                          <div id="admin-forward-charges-section" className="space-y-1.5 pt-1">
                            <div className="flex justify-between items-center">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 font-sans">
                                Charges Breakdown
                              </label>
                              <button
                                type="button"
                                onClick={handleAddCharge}
                                className="text-[#be001e] hover:text-[#e6202f] font-extrabold text-[10px] uppercase tracking-wider flex items-center gap-1 cursor-pointer font-sans disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={sendMutation.isPending}
                              >
                                <Plus className="size-3" /> Add Charge Row
                              </button>
                            </div>

                            {formError && (
                              <div className="flex items-center gap-1.5 text-xs font-semibold text-[#e6202f] bg-red-50 p-2.5 rounded-lg border border-red-100 animate-in fade-in duration-200 font-sans my-2">
                                <AlertCircle className="size-4 shrink-0" />
                                <span>{formError}</span>
                              </div>
                            )}

                            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm font-sans">
                              <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                  <tr>
                                    <th className="p-3">Description</th>
                                    <th className="p-3 text-center w-32">Unit Price</th>
                                    <th className="p-3 text-center w-24">Quantity</th>
                                    <th className="p-3 text-center w-28">Unit</th>
                                    <th className="p-3 text-center w-28">Discount (%)</th>
                                    <th className="p-3 text-center w-24">Tax (%)</th>
                                    <th className="p-3 text-center w-24">Tax Type</th>
                                    <th className="p-3 text-right">Total</th>
                                    <th className="p-3 w-10"></th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 bg-white">
                                  {formCharges.map((charge, idx) => {
                                    const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1
                                    const base = (Number(charge.unitPrice) || 0) * qty
                                    const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                                    const total = subtotal + (subtotal * ((Number(charge.tax) || 0) / 100))

                                    const descError = getChargeDescriptionError(charge.description) || (!charge.description.trim() && formError ? "Description is required" : null)
                                    const unitPriceError = getChargeUnitPriceError(charge.unitPrice) || (charge.unitPrice === "" && formError ? "Unit price is required" : null)
                                    const quantityError = getChargeQuantityError(charge.quantity, charge.unit)
                                    const unitError = getChargeUnitError(charge.unit, charge.quantity)
                                    const discountError = getChargeDiscountError(charge.discount)
                                    const taxError = getChargeTaxError(charge.tax)

                                    const isRowDisabled = idx > 0 && !formCharges.slice(0, idx).every(isChargeRowCompleteAndValid)

                                    return (
                                      <tr key={idx} className={cn("hover:bg-slate-50/50 transition-colors align-top", isRowDisabled && "bg-slate-50/40 opacity-70")}>
                                        <td className="p-2 min-w-[200px]">
                                          <input
                                            type="text"
                                            value={charge.description}
                                            onChange={(e) => handleChargeChange(idx, "description", e.target.value)}
                                            placeholder="e.g. Shipping Fee, Customs"
                                            className={cn(
                                              "w-full rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-200",
                                              descError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            required
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {descError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1">
                                              {descError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-32">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={charge.unitPrice}
                                            onChange={(e) => handleChargeChange(idx, "unitPrice", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.unitPrice)}
                                            placeholder="0.00"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              unitPriceError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            required
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {unitPriceError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {unitPriceError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-24">
                                          <input
                                            type="text"
                                            inputMode={isIntegerUnit(charge.unit) ? "numeric" : "decimal"}
                                            value={charge.quantity}
                                            onChange={(e) => handleChargeChange(idx, "quantity", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.quantity, isIntegerUnit(charge.unit))}
                                            placeholder="0"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              quantityError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {quantityError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {quantityError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-28">
                                          <button
                                            type="button"
                                            data-dropdown-trigger={`forward-unit-${idx}`}
                                            onClick={(e) => {
                                              if (isRowDisabled) return
                                              const rect = e.currentTarget.getBoundingClientRect()
                                              setForwardUnitDropdownPosition({
                                                top: rect.bottom,
                                                left: rect.left,
                                                width: rect.width
                                              })
                                              setOpenForwardUnitDropdownIndex(prev => prev === idx ? null : idx)
                                            }}
                                            className={cn(
                                              "w-full flex items-center justify-between rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                              unitError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          >
                                            <span className="truncate">
                                              {charge.unit ? (QUANTITY_UNITS.find(u => u.code === charge.unit)?.name || charge.unit) : "Select"}
                                            </span>
                                            <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openForwardUnitDropdownIndex === idx && "rotate-180")} />
                                          </button>

                                          {openForwardUnitDropdownIndex === idx && forwardUnitDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                            <ul 
                                              ref={forwardUnitDropdownMenuRef}
                                              style={{
                                                position: 'fixed',
                                                top: `${forwardUnitDropdownPosition.top + 4}px`,
                                                left: `${forwardUnitDropdownPosition.left}px`,
                                                width: `${forwardUnitDropdownPosition.width}px`,
                                              }}
                                              className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                            >
                                              <li
                                                onClick={() => {
                                                  handleChargeChange(idx, "unit", "")
                                                  setOpenForwardUnitDropdownIndex(null)
                                                }}
                                                className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate"
                                              >
                                                None
                                              </li>
                                              {QUANTITY_UNITS.map((unitOpt) => (
                                                <li
                                                  key={unitOpt.code}
                                                  onClick={() => {
                                                    handleChargeChange(idx, "unit", unitOpt.code)
                                                    setOpenForwardUnitDropdownIndex(null)
                                                  }}
                                                  className={cn(
                                                    "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                                    charge.unit === unitOpt.code && "bg-[#be001e]/5 text-[#be001e] font-bold"
                                                  )}
                                                >
                                                  {unitOpt.name}
                                                </li>
                                              ))}
                                            </ul>,
                                            document.body
                                          )}
                                          {unitError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {unitError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-28">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={charge.discount}
                                            onChange={(e) => handleChargeChange(idx, "discount", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.discount)}
                                            placeholder="0"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              discountError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {discountError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {discountError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-24">
                                          <input
                                            type="text"
                                            inputMode="decimal"
                                            value={charge.tax}
                                            onChange={(e) => handleChargeChange(idx, "tax", e.target.value)}
                                            onKeyDown={(e) => blockNegativeKeys(e, charge.tax)}
                                            placeholder="18"
                                            className={cn(
                                              "w-full text-center rounded-lg border bg-white px-2.5 py-2 text-xs text-slate-800 focus:outline-none transition-all duration-200 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                                              taxError
                                                ? "border-[#be001e] focus:border-[#be001e] ring-1 ring-[#be001e]/10"
                                                : "border-slate-200 focus:border-[#be001e]",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          />
                                          {taxError && !isRowDisabled && (
                                            <p className="text-[10px] text-[#be001e] font-semibold mt-1 text-center whitespace-normal">
                                              {taxError}
                                            </p>
                                          )}
                                        </td>
                                        <td className="p-2 w-24">
                                          <button
                                            type="button"
                                            data-dropdown-trigger={`forward-taxtype-${idx}`}
                                            onClick={(e) => {
                                              if (isRowDisabled) return
                                              const rect = e.currentTarget.getBoundingClientRect()
                                              setForwardTaxTypeDropdownPosition({
                                                top: rect.bottom,
                                                left: rect.left,
                                                width: rect.width
                                              })
                                              setOpenForwardTaxTypeDropdownIndex(prev => prev === idx ? null : idx)
                                            }}
                                            className={cn(
                                              "w-full flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 focus:border-[#be001e] focus:outline-none transition-all duration-200 h-[34px] cursor-pointer text-left",
                                              isRowDisabled && "disabled:opacity-50 disabled:cursor-not-allowed bg-slate-50"
                                            )}
                                            disabled={sendMutation.isPending || isRowDisabled}
                                            title={isRowDisabled ? "Complete previous row validation first" : undefined}
                                          >
                                            <span className="truncate">
                                              {charge.taxType === "NONE" ? "None" : (charge.taxType || "GST")}
                                            </span>
                                            <ChevronDown className={cn("size-3.5 text-slate-400 transition-transform shrink-0 ml-1", openForwardTaxTypeDropdownIndex === idx && "rotate-180")} />
                                          </button>

                                          {openForwardTaxTypeDropdownIndex === idx && forwardTaxTypeDropdownPosition && !isRowDisabled && typeof document !== "undefined" && createPortal(
                                            <ul 
                                              ref={forwardTaxTypeDropdownMenuRef}
                                              style={{
                                                position: 'fixed',
                                                top: `${forwardTaxTypeDropdownPosition.top + 4}px`,
                                                left: `${forwardTaxTypeDropdownPosition.left}px`,
                                                width: `${forwardTaxTypeDropdownPosition.width}px`,
                                              }}
                                              className="z-[99999] max-h-[140px] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl overscroll-contain py-1"
                                            >
                                              {[
                                                { value: "GST", label: "GST" },
                                                { value: "VAT", label: "VAT" },
                                                { value: "NONE", label: "None" }
                                              ].map((option) => (
                                                <li
                                                  key={option.value}
                                                  onClick={() => {
                                                    handleChargeChange(idx, "taxType", option.value)
                                                    setOpenForwardTaxTypeDropdownIndex(null)
                                                  }}
                                                  className={cn(
                                                    "px-3 py-1.5 text-xs font-medium text-slate-750 hover:bg-[#be001e]/5 hover:text-[#be001e] cursor-pointer transition-colors border-b border-slate-50 last:border-0 truncate",
                                                    charge.taxType === option.value && "bg-[#be001e]/5 text-[#be001e] font-bold"
                                                  )}
                                                >
                                                  {option.label}
                                                </li>
                                              ))}
                                            </ul>,
                                            document.body
                                          )}
                                        </td>
                                        <td className="p-2 text-center font-black text-slate-800 align-middle">
                                          <div className="flex items-center justify-center min-h-[34px] font-mono font-bold">
                                            {Math.max(0, total).toFixed(2)}
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <div className="flex items-center justify-center min-h-[34px]">
                                            {formCharges.length > 1 && (
                                              <button
                                                type="button"
                                                onClick={() => handleRemoveCharge(idx)}
                                                className="p-1.5 rounded-md border border-red-100 bg-red-50 hover:bg-red-100 text-[#e6202f] transition-all cursor-pointer shadow-sm"
                                                title="Remove charge"
                                                disabled={sendMutation.isPending}
                                              >
                                                <X className="size-3.5" />
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm font-sans">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                              <span className="text-[12px] font-bold text-[#be001e] font-mono">
                                {currencyCode} {formCharges.reduce((acc, charge) => {
                                  const qty = (charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1
                                  const base = (Number(charge.unitPrice) || 0) * qty
                                  const subtotal = base - (base * ((Number(charge.discount) || 0) / 100))
                                  return acc + Math.max(0, subtotal + (subtotal * ((Number(charge.tax) || 0) / 100)));
                                }, 0).toFixed(2)}
                              </span>
                            </div>
                          </div>

                          <div className="flex gap-2.5 justify-end pt-1">
                            <button
                              type="button"
                              onClick={onBack}
                              disabled={sendMutation.isPending}
                              className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer border border-slate-200 transition-colors disabled:opacity-50 font-sans"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={sendMutation.isPending}
                              className="px-3 py-1.5 bg-[#be001e] hover:bg-[#e6202f] text-white text-xs font-bold rounded-lg cursor-pointer flex items-center gap-1.5 transition-colors disabled:opacity-50 font-sans"
                            >
                              {sendMutation.isPending ? (
                                <>
                                  <Loader2 className="size-3 animate-spin font-sans" />
                                  Forwarding...
                                </>
                              ) : (
                                <>Forward Quotation</>
                              )}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-[#be001e1a] lg:pl-6 text-left">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="size-3.5 text-[#e6202f]" /> Timestamps
              </h4>

              <div className="relative pl-5 space-y-4 font-sans">
                <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-[#be001e1a]"></div>

                <div className="relative flex gap-2.5 items-start">
                  <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-[#e0f2fe]">
                    <div className="size-1.5 rounded-full bg-sky-600"></div>
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">
                      {activeTab === "seller" ? "RECEIVED DATE" : "SENT DATE"}
                    </span>
                    <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(quotation.createdAt)}</p>
                  </div>
                </div>

                {(quotation.expiresAt || quotation.validUntil) && (
                  <div className="relative flex gap-2.5 items-start">
                    <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-amber-100 bg-[#fef3c7]">
                      <div className="size-1.5 rounded-full bg-amber-600"></div>
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">VALID UNTIL</span>
                      <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(quotation.expiresAt || quotation.validUntil)}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {(selectedDetailVariant || selectedDetailVariantId) && (
                <VariantDetailSidebar
                  variant={selectedDetailVariant || undefined}
                  variantId={selectedDetailVariantId || undefined}
                  onClose={() => {
                    setSelectedDetailVariant(null)
                    setSelectedDetailVariantId(null)
                  }}
                  onCompanyClick={(c) => setSelectedCompany(c)}
                />
              )}
              {selectedProduct && (
                <ProductDetailSidebar
                  product={selectedProduct}
                  onClose={() => setSelectedProduct(null)}
                  onCompanyClick={(c) => setSelectedCompany(c)}
                />
              )}
              {selectedCompany && (
                <CompanyDetailSidebar 
                  company={selectedCompany} 
                  onClose={() => setSelectedCompany(null)} 
                  level={selectedProduct || selectedDetailVariant ? 1 : 0}
                />
              )}
              {selectedExternalSeller && (
                <ExternalSellerDetailSidebar 
                  externalSeller={selectedExternalSeller} 
                  onClose={() => setSelectedExternalSeller(null)} 
                />
              )}
              {selectedRfq && (
                <RfqDetailSidebar 
                  rfq={{
                    ...selectedRfq,
                    company: selectedRfq.company || quotation?.company
                  }}
                  onClose={() => setSelectedRfq(null)} 
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Empty space on mobile only when forwarding or revising form is open so user can scroll above keyboard */}
      {isEditing && (
        <div className="h-16 sm:h-8 md:hidden pointer-events-none" aria-hidden="true" />
      )}
    </div>
  )
}

export function QuotationDetailsPage({ quotationId, subTab }: { quotationId: string; subTab: "seller" | "buyer" }) {
  const accessToken = useAuthStore((s) => s.accessToken)
  const navigate = useNavigate()
  const activeTab = subTab

  const triggerToast = (msg: string) => {
    toast.success(msg)
  }

  const {
    data: buyerDetailData,
    isLoading: isBuyerDetailLoading,
    isError: isBuyerDetailError,
    error: buyerDetailError,
  } = useGetAdminToBuyerQuotationById(
    (activeTab === "buyer" && quotationId) ? quotationId : ""
  )

  const {
    data: sellerDetailData,
    isLoading: isSellerDetailLoading,
    isError: isSellerDetailError,
    error: sellerDetailError,
  } = useGetSellerQuotationById(
    (activeTab === "seller" && quotationId) ? quotationId : "",
    activeTab === "seller" && !!quotationId
  )

  const isDetailLoading = activeTab === "buyer" ? isBuyerDetailLoading : isSellerDetailLoading
  const isDetailError = activeTab === "buyer" ? isBuyerDetailError : isSellerDetailError
  const detailError = activeTab === "buyer" ? buyerDetailError : sellerDetailError

  const selectedQuotation = activeTab === "buyer"
    ? (buyerDetailData?.adminToBuyerQuotation ?? buyerDetailData)
    : (sellerDetailData?.sellerToAdminQuotation ?? sellerDetailData)

  const handleGoBack = () => {
    navigate({ to: "/quotations", search: { subTab } })
  }

  const handleRevised = (newId: string) => {
    triggerToast("Quotation revised successfully!")
    navigate({ to: "/quotations/$quotationId", params: { quotationId: newId }, search: { subTab: "buyer" } })
  }

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-[#be001e]/20 rounded-2xl shadow-sm max-w-5xl mx-auto font-sans text-left animate-in fade-in duration-200">
        <button
          onClick={handleGoBack}
          className="absolute top-4 left-4 flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
          title="Back to Quotations"
        >
          <ChevronLeft className="size-4" />
        </button>
        <Loader2 className="size-8 animate-spin text-[#e6202f]" />
        <p className="text-sm font-medium text-slate-500 font-sans">Fetching quotation details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Quotations"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700 font-sans">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold font-sans">{(detailError as Error)?.message || "Failed to load quotation details."}</p>
        </div>
      </div>
    )
  }

  if (!selectedQuotation) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left animate-in fade-in duration-200">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Quotations"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700 font-sans">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold font-sans">Quotation details not found.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 relative">
      <QuotationDetailView
        quotation={selectedQuotation}
        activeTab={activeTab}
        onBack={handleGoBack}
        onForwardSuccess={triggerToast}
        onRevised={handleRevised}
      />
    </div>
  )
}
